import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Copy, User, Link as LinkIcon, CheckCircle } from 'lucide-react';
import SchemaOrgArticle from './SchemaOrgArticle';
import CardContainer from './CardContainer';
import { MobileDetailView, MobileButton } from './mobile';

const OpinionDetailView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [opinion, setOpinion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchOpinion = async () => {
      try {
        setLoading(true);
        
        // The slug should contain the ID at the end (e.g., 'my-opinion-title-123')
        // Extract ID from the slug if present
        const slugParts = slug.split('-');
        const potentialId = slugParts[slugParts.length - 1];
        const isIdNumeric = /^\d+$/.test(potentialId);
        
        let response;
        // If the last part of the slug looks like an ID, use it directly
        if (isIdNumeric) {
          response = await fetch(`/api/opinions/${potentialId}`);
        } 
        // Otherwise use the slug endpoint
        else {
          response = await fetch(`/api/opinions/slug/${slug}`);
        }
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Opinion not found');
          }
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // For unpublished opinions, only allow viewing if they're explicitly accessed
        if (data.status !== 'published' && !window.location.pathname.includes('/admin/')) {
          navigate('/o/');
          return;
        }
        
        setOpinion(data);
        setError(null);
        
        // Update title and meta tags
        document.title = `${data.title} | Opinions`;
      } catch (err) {
        setError(`Failed to load opinion: ${err.message}`);
        setOpinion(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchOpinion();
  }, [slug, navigate]);

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (e) {
      return dateString;
    }
  };
  
  // Format date for mobile display (shorter format)
  const formatMobileDate = (dateString) => {
    try {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (e) {
      return dateString;
    }
  };

  // Copy current URL to clipboard
  const copyToClipboard = () => {
    try {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      
      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12 sm:py-20">
        <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-2 border-b-2 border-slate-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 sm:py-10 px-4">
        <div className="text-red-500 font-medium mb-4">{error}</div>
        <Link to="/o/" className="text-slate-600 hover:text-slate-800 flex items-center justify-center">
          <ArrowLeft size={18} className="mr-1" />
          Back to Opinions
        </Link>
      </div>
    );
  }

  if (!opinion) {
    return (
      <div className="text-center py-8 sm:py-10 px-4">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-700 mb-4">Opinion not found</h2>
        <Link to="/o/" className="text-slate-600 hover:text-slate-800 flex items-center justify-center">
          <ArrowLeft size={18} className="mr-1" />
          Back to Opinions
        </Link>
      </div>
    );
  }
  
  // Content for the detail view
  const OpinionContent = () => (
    <CardContainer className="shadow-sm sm:shadow-md w-full" fullWidth={true}>
      <div className="p-3 sm:p-6 w-full">
        {/* Title is now inside the card */}
        <h1 className="text-xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
          {opinion.title}
        </h1>
      
        {/* Author and publication details */}
        <div className="flex flex-wrap items-center text-xs sm:text-sm text-gray-600 gap-x-3 sm:gap-x-4 gap-y-2 mb-4 sm:mb-6 border-b border-gray-100 pb-3 sm:pb-4 bg-gray-50 rounded-md p-2 sm:p-3 w-full">
          <div className="flex items-center">
            <User size={14} className="mr-1 text-gray-500" />
            <span className="font-medium">{opinion.author}</span>
          </div>
          
          <div className="flex items-center">
            <Calendar size={14} className="mr-1 text-gray-500" />
            <span className="hidden sm:inline">{formatDate(opinion.publication_date)}</span>
            <span className="sm:hidden">{formatMobileDate(opinion.publication_date)}</span>
          </div>
          
          <div className="flex items-center">
            <Clock size={14} className="mr-1 text-gray-500" />
            <span>{opinion.reading_time_minutes} min read</span>
          </div>
          
          {opinion.source_url && (
            <div className="flex items-center">
              <LinkIcon size={14} className="mr-1 text-gray-500" />
              <a 
                href={opinion.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-600 hover:text-slate-800 mobile-touch-target"
              >
                Source Article
              </a>
            </div>
          )}
        </div>
        
        <article>
          {/* Meta description */}
          {opinion.meta_description && (
            <div className="text-base sm:text-lg text-gray-600 italic border-l-4 border-gray-200 pl-3 sm:pl-4 py-2 mb-4 sm:mb-6">
              {opinion.meta_description}
            </div>
          )}
          
          {/* Featured image */}
          {opinion.meta_image_url && (
            <div className="mb-6 sm:mb-8 -mx-4 sm:mx-0">
              <img
                src={opinion.meta_image_url}
                alt={opinion.title}
                className="w-full h-auto sm:rounded-lg shadow-sm"
                loading="lazy"
              />
            </div>
          )}
          
          {/* Opinion content */}
          <div 
            className="prose max-w-none w-full"
            dangerouslySetInnerHTML={{ 
              __html: `<div style="line-height: 1.5; width: 100%;">
                ${opinion.content
                  .replace(/\n+/g, ' ')
                  .replace(/<\/p>\s*<p>/g, '</p><p>')
                  .replace(/<p>\s*<\/p>/g, '')
                }
              </div>`
            }}
          />
          
          {/* Footer with updated date if different from published date */}
          {opinion.last_updated_date !== opinion.publication_date && (
            <div className="mt-6 sm:mt-8 pt-3 sm:pt-4 border-t border-gray-200 text-xs sm:text-sm text-gray-500">
              Last updated on {formatDate(opinion.last_updated_date)}
            </div>
          )}
        </article>
        
        {/* Copy Link button at bottom */}
        <div className="flex justify-center sm:justify-end mt-6 sm:mt-8">
          <MobileButton
            variant="secondary"
            size="small"
            leftIcon={copied ? <CheckCircle size={16} /> : <Copy size={16} />}
            onClick={copyToClipboard}
            className="min-w-[150px] sm:min-w-[180px]"
          >
            {copied ? 'Copied!' : 'Copy Article Link'}
          </MobileButton>
        </div>
      </div>
    </CardContainer>
  );

  return (
    <>
      {/* Schema.org structured data */}
      <SchemaOrgArticle
        title={opinion.title}
        datePublished={opinion.publication_date}
        dateModified={opinion.last_updated_date}
        authorName={opinion.author}
        description={opinion.meta_description || ''}
        imageUrl={opinion.meta_image_url || ''}
        isOpinionPiece={true}
      />
      
      {/* Mobile-optimized detail view */}
      <MobileDetailView
        backUrl="/o/"
        backLabel="Opinions"
        contentClassName="w-full"
        className="w-full"
      >
        <OpinionContent />
      </MobileDetailView>
    </>
  );
};

export default OpinionDetailView;